import { useState } from "react";
import { useClassManagement } from "@/hooks/useClassManagement";
import { useProfile } from "@/hooks/useProfile";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { GymList } from "./classes/GymList";
import { ClassDialogs } from "./classes/ClassDialogs";

interface ClassesManagerProps {
  gymId?: string;
}

export const ClassesManager = ({ gymId }: ClassesManagerProps) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedGymId, setSelectedGymId] = useState<string | null>(null);
  const [openGyms, setOpenGyms] = useState<Record<string, boolean>>({});
  const { classes, handleSubmit, handleDelete } = useClassManagement(selectedGymId);
  const { profile } = useProfile();

  const { data: linkedGyms } = useQuery({
    queryKey: ["linked-gyms", profile?.id],
    queryFn: async () => {
      if (profile?.role === "manager") {
        const { data: gymManagers } = await supabase
          .from("gym_managers")
          .select("gym_id, gyms:gyms(*)")
          .eq("manager_id", profile.id);
        return gymManagers?.map(gm => gm.gyms) || [];
      } else if (profile?.gym_id) {
        const { data: gym } = await supabase
          .from("gyms")
          .select("*")
          .eq("id", profile.gym_id)
          .single();
        return gym ? [gym] : [];
      }
      return [];
    },
    enabled: !!profile,
  });

  const handleFormSubmit = async (formData: any) => {
    console.log("🔧 handleFormSubmit called", {
      editingId,
      selectedGymId,
      isCreating,
      formData,
      userRole: profile?.role
    });

    // Modo EDICIÓN: obtener gym_id de la clase que se está editando
    if (editingId) {
      const classBeingEdited = classes?.find(c => c.id === editingId);
      if (!classBeingEdited?.gym_id) {
        console.error("Cannot find gym_id for class being edited");
        return false;
      }
      
      // Pasar gym_id al formData para asegurar que se preserve
      const updatedFormData = {
        ...formData,
        gym_id: classBeingEdited.gym_id
      };
      
      const success = await handleSubmit(updatedFormData, editingId);
      if (success) {
        setEditingId(null);
        setIsCreating(false);
      }
      return success;
    }
    
    // Modo CREACIÓN: validar que hay gym seleccionado
    if (profile?.role === "manager") {
      if (!selectedGymId && !linkedGyms?.length) {
        console.error("Manager has no gyms assigned");
        return false;
      }
      if (!selectedGymId) {
        console.error("No gym selected for class creation");
        return false;
      }
    }
    
    // Para creación, usar selectedGymId
    const success = await handleSubmit(formData, null);
    if (success) {
      setEditingId(null);
      setIsCreating(false);
    }
    return success;
  };

  const filteredClasses = classes?.filter(class_ => {
    if (profile?.role === 'profesor') {
      const instructorName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
      return class_.instructor === instructorName;
    } else if (profile?.role === 'manager') {
      // Managers can only see classes from gyms they manage
      const managedGymIds = linkedGyms?.map(gym => gym.id) || [];
      return managedGymIds.includes(class_.gym_id);
    }
    return true;
  });

  const toggleGym = (gymId: string) => {
    setOpenGyms(prev => ({
      ...prev,
      [gymId]: !prev[gymId]
    }));
  };

  const handleAddClass = (gymId: string) => {
    setSelectedGymId(gymId);
    setIsCreating(true);
  };

  console.log("📋 ClassesManager state:", {
    editingId,
    selectedGymId,
    classesCount: classes?.length,
    filteredClassesCount: filteredClasses?.length,
    linkedGymsCount: linkedGyms?.length,
    userRole: profile?.role
  });

  return (
    <div className="space-y-6">
      <GymList
        linkedGyms={linkedGyms}
        filteredClasses={filteredClasses}
        openGyms={openGyms}
        onToggleGym={toggleGym}
        onAddClass={handleAddClass}
        onEditClass={(class_) => setEditingId(class_.id)}
        onDeleteClass={handleDelete}
        onSelectClass={setSelectedClassId}
        userRole={profile?.role}
      />

      <ClassDialogs
        editingId={editingId}
        selectedClassId={selectedClassId}
        isCreating={isCreating}
        classes={classes}
        selectedGymId={selectedGymId}
        onCloseEdit={() => setEditingId(null)}
        onCloseCreate={setIsCreating}
        onCloseSelected={() => setSelectedClassId(null)}
        onSubmit={handleFormSubmit}
      />
    </div>
  );
};